import * as React from 'react';

import { IconRegularClose } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';

import ErrorBoundary from '@components/ErrorBoundary';
import PriceLabel from '@components/PriceLabel';
import { getSupportedAttributes } from '@components/Tags/model';
import { IVariantsProps } from '@containers/ProductDetailContainer/model';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { variantInfo, dataLabelsProp } from '@helpers/utils';
import { HorizontalChevron } from '@sharedComponents/Icons/HorizontalChevron';

import { useIsDualPrice } from '../../hooks/useIsDualPrice';
import { usePriceWithVoucher } from '../hooks/usePriceWithVoucher';
import { useVariantTransition } from '../Variants/useVariantTransition';

import { ExpandedVariants } from './ExpandedVariants';
import {
  Wrapper,
  Select,
  SelectedVariantName,
  Text,
  CloseIcon as CloseIconWrapper,
  CloseWrapper,
  ChevronIconWrapper,
  PriceAndName,
  Prices,
  PriceWrapper,
  CurrencyWrapper,
  DualPriceWrapper,
  DualPrice,
  DualPriceSplitter,
} from './styled';
import { trackSelectOpen } from './tracking';
import { VariantItem } from './VariantItem';

export type IVariantsInSelectboxProps = IVariantsProps;

export const VariantsInSelectbox = ({
  variants,
  selectedId: _selectedId,
  locale,
}: IVariantsInSelectboxProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { getTimeFromInit } = useTrackingContext();
  const supportedAttributes = getSupportedAttributes();
  const {
    isDualPrice,
    getLeftSideFormattedPrice,
    getRightSideFormattedPrice,
    leftSideCurrency,
    rightSideCurrency,
  } = useIsDualPrice();

  const { handleClick, selectedId } = useVariantTransition(_selectedId);

  const handleExpand = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!isExpanded) {
      trackSelectOpen(getTimeFromInit());
    }
    setIsExpanded(!isExpanded);
  };

  const { showGroups, selectedGroup } = useProductDetailContext().variantGroups;
  const variantsToShow = showGroups ? selectedGroup!.variants : variants;

  React.useEffect(() => {
    setIsExpanded(false);
  }, [variants, selectedId]);

  const selectedVariant = variants.find(
    (variant) => variant.webId === selectedId
  );
  const price = usePriceWithVoucher(selectedVariant!);

  const text = variantInfo(
    selectedVariant as Parameters<typeof variantInfo>[0],
    showGroups
  );
  const inStock = selectedVariant?.stockAvailability.code !== IStock.outOfStock;

  return (
    <ErrorBoundary>
      <Wrapper data-testid="pd-variants-select-box" id="pdVariantsSelectbox">
        <Select
          onClick={handleExpand}
          className="pd-variant-selected"
          aria-live="assertive"
          tabIndex={0}
          isExpanded={isExpanded}
          {...dataLabelsProp({
            attributes: selectedVariant!.attributes,
            supportedAttributes,
          })}
        >
          <PriceAndName>
            {inStock && !isDualPrice && (
              <Prices>
                <PriceLabel
                  id="pd-price"
                  price={{
                    value: price.value ?? 0,
                    currency: price.currency ?? undefined,
                  }}
                  wrapperComponent={PriceWrapper}
                  currencyComponent={CurrencyWrapper}
                />
              </Prices>
            )}

            {inStock && isDualPrice && (
              <Prices>
                <DualPriceWrapper data-testid="pd-price" id="pd-dual-price">
                  <DualPrice>
                    {getLeftSideFormattedPrice(price.value ?? 0)}{' '}
                    <span>{leftSideCurrency}</span>
                  </DualPrice>
                  <DualPriceSplitter>/</DualPriceSplitter>
                  <DualPrice>
                    {getRightSideFormattedPrice(price.value ?? 0)}{' '}
                    <span>{rightSideCurrency}</span>
                  </DualPrice>
                </DualPriceWrapper>
              </Prices>
            )}

            <SelectedVariantName>
              <Text title={text}>{text}</Text>
            </SelectedVariantName>
          </PriceAndName>
          <ChevronIconWrapper type="button">
            <HorizontalChevron
              isOpened={isExpanded}
              width="1.25rem"
              height="1.25rem"
              color="icon.secondary"
            />
          </ChevronIconWrapper>
        </Select>

        {isExpanded && (
          <ExpandedVariants isExpanded={isExpanded} onOuterClick={handleExpand}>
            <CloseWrapper>
              <CloseIconWrapper onClick={handleExpand}>
                <IconRegularClose
                  color="icon.tertiary"
                  width="1.25rem"
                  height="1.25rem"
                />
              </CloseIconWrapper>
            </CloseWrapper>
            {variantsToShow.map((variant) => (
              <VariantItem
                variant={variant}
                locale={locale}
                isSelected={variant.webId === selectedId}
                key={variant.webId}
                onClick={handleClick}
              />
            ))}
          </ExpandedVariants>
        )}
      </Wrapper>
    </ErrorBoundary>
  );
};
